import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FileText, ArrowLeft, Copy, CheckCircle, Loader2,
  Download, Globe, Calendar, Printer
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import AppLayout from '../components/AppLayout';
import { useTheme } from '../contexts/ThemeContext';
import { sanitizeHTML } from '../lib/sanitize';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PressReleaseView() {
  const { pressReleaseId } = useParams();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [pressRelease, setPressRelease] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchPressRelease();
  }, [pressReleaseId]);

  const fetchPressRelease = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/press-releases/${pressReleaseId}`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Press release not found');
        }
        throw new Error('Failed to load press release');
      }
      
      const data = await response.json();
      setPressRelease(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(pressRelease.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleDownload = () => {
    const blob = new Blob([pressRelease.content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `press-release-${pressReleaseId}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Press Release</title>
          <style>
            body {
              font-family: Georgia, 'Times New Roman', Times, serif;
              max-width: 800px;
              margin: 40px auto;
              padding: 20px;
              line-height: 1.8;
              color: #1a1a1a;
            }
            h1, h2, h3 {
              font-family: Arial, sans-serif;
              margin-top: 1.5em;
            }
            p {
              margin-bottom: 1em;
            }
            @media print {
              body { margin: 0; padding: 20px; }
            }
          </style>
        </head>
        <body>
          ${sanitizeHTML(pressRelease.content.replace(/\n/g, '<br>'))}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  if (loading) {
    return (
      <AppLayout title="Press Release">
        <div className="flex items-center justify-center py-20">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-[#A2FF00]" />
            <p className={isDark ? 'text-zinc-400' : 'text-gray-600'}>Loading press release...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (error) {
    return (
      <AppLayout title="Press Release">
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
          <FileText className={`h-16 w-16 mx-auto mb-4 ${isDark ? 'text-zinc-600' : 'text-gray-400'}`} />
          <h2 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Error</h2>
          <p className={`mb-6 ${isDark ? 'text-zinc-400' : 'text-gray-600'}`}>{error}</p>
          <Button
            onClick={() => navigate('/press-releases')}
            className="bg-[#A2FF00] text-black hover:bg-[#8FE600]"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Press Releases
          </Button>
        </div>
      </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Press Release">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/press-releases')}
            className={isDark ? 'text-zinc-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopy}
              className={isDark 
                ? 'border-zinc-700 text-zinc-300 hover:bg-zinc-800' 
                : 'border-gray-300 text-gray-700 hover:bg-gray-100'}
            >
              {copied ? (
                <>
                  <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 mr-2" />
                  Copy
                </>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownload}
              className={isDark 
                ? 'border-zinc-700 text-zinc-300 hover:bg-zinc-800' 
                : 'border-gray-300 text-gray-700 hover:bg-gray-100'}
            >
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrint}
              className={isDark 
                ? 'border-zinc-700 text-zinc-300 hover:bg-zinc-800' 
                : 'border-gray-300 text-gray-700 hover:bg-gray-100'}
            >
              <Printer className="h-4 w-4 mr-2" />
              Print
            </Button>
          </div>
        </div>

        {/* Meta Info Card */}
        <Card className={`mb-6 ${isDark ? 'bg-zinc-900/50 border-zinc-800' : 'bg-white border-gray-200 shadow-sm'}`}>
          <CardContent className="py-4">
            <div className="flex flex-wrap items-center gap-6 text-sm">
              <div className={`flex items-center gap-2 ${isDark ? 'text-zinc-400' : 'text-gray-600'}`}>
                <Globe className="h-4 w-4" />
                <a 
                  href={pressRelease.company_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-[#A2FF00] transition-colors"
                >
                  {pressRelease.company_url}
                </a>
              </div>
              <div className={`flex items-center gap-2 ${isDark ? 'text-zinc-400' : 'text-gray-600'}`}>
                <Calendar className="h-4 w-4" />
                <span>
                  {new Date(pressRelease.created_at).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Press Release Content */}
        <Card className={isDark ? 'bg-zinc-900/50 border-zinc-800' : 'bg-white border-gray-200 shadow-sm'}>
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              <FileText className="h-5 w-5 text-[#A2FF00]" />
              Generated Press Release
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div 
              className={`prose max-w-none ${isDark ? 'prose-invert' : ''}`}
              style={{
                fontFamily: "Georgia, 'Times New Roman', Times, serif",
                lineHeight: '1.8'
              }}
            >
              {pressRelease.content.split('\n').map((paragraph, idx) => {
                // Handle headings
                if (paragraph.startsWith('# ')) {
                  return <h1 key={idx} className={`text-2xl font-bold mt-6 mb-4 font-sans ${isDark ? 'text-white' : 'text-gray-900'}`}>{paragraph.substring(2)}</h1>;
                }
                if (paragraph.startsWith('## ')) {
                  return <h2 key={idx} className={`text-xl font-bold mt-5 mb-3 font-sans ${isDark ? 'text-white' : 'text-gray-900'}`}>{paragraph.substring(3)}</h2>;
                }
                if (paragraph.startsWith('### ')) {
                  return <h3 key={idx} className={`text-lg font-bold mt-4 mb-2 font-sans ${isDark ? 'text-white' : 'text-gray-900'}`}>{paragraph.substring(4)}</h3>;
                }
                // Handle bold text (for "About" sections)
                if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                  return <h3 key={idx} className={`text-lg font-bold mt-4 mb-2 font-sans ${isDark ? 'text-white' : 'text-gray-900'}`}>{paragraph.slice(2, -2)}</h3>;
                }
                // Empty lines
                if (paragraph.trim() === '') {
                  return <div key={idx} className="h-4" />;
                }
                // Regular paragraphs
                return (
                  <p 
                    key={idx} 
                    className={`mb-4 ${isDark ? 'text-zinc-200' : 'text-gray-700'}`}
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHTML(paragraph
                        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                        .replace(/\*(.*?)\*/g, '<em>$1</em>'))
                    }}
                  />
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Action Bar */}
        <div className="mt-6 flex justify-center">
          <Button
            onClick={() => navigate('/press-releases')}
            className="bg-[#A2FF00] text-black hover:bg-[#8FE600]"
          >
            Generate Another Press Release
          </Button>
        </div>
      </div>
    </AppLayout>
  );
}
